import { FLASHCARD_FRAMES } from '@/lib/flashcard-frames';
import { CATEGORY_STYLE, type WebResource } from '@/content/resources';
import { HubCarouselScroller } from '@/components/hub/HubCarouselScroller';
import { HERO_CARD_WIDTH, HUB_CAROUSEL_TRACK } from '@/components/hub/hubCarousel';
import flappyBgUrl from '@/assets/games/flappy-hmong/bg.png';
import flappyBirdUrl from '@/assets/games/flappy-hmong/bird.png';
import { typeScale } from '@/lib/typography';

export type HeroSlide = {
  href: string;
  title: string;
  category: WebResource['category'];
  imageUrl?: string | null;
  premium?: boolean;
};

type HeroCarouselProps = {
  slides: HeroSlide[];
  onOpen: (slide: HeroSlide) => void;
};

const FLASH_FRAMES = [
  FLASHCARD_FRAMES.find((frame) => frame.id === 'LIGHT_PURPLE') ?? FLASHCARD_FRAMES[10],
  FLASHCARD_FRAMES.find((frame) => frame.id === 'LIGHT_BLUE') ?? FLASHCARD_FRAMES[12],
  FLASHCARD_FRAMES.find((frame) => frame.id === 'LIGHT_ORANGE') ?? FLASHCARD_FRAMES[11],
];

export function HeroCarousel({ slides, onOpen }: HeroCarouselProps) {
  if (!slides.length) return null;
  return (
    <section className="min-w-0" data-testid="learning-hub-hero">
      <HubCarouselScroller testId="learning-hub-hero-scroller">
        <div data-testid="learning-hub-hero-track" className={`${HUB_CAROUSEL_TRACK} gap-3`}>
          {slides.map((slide) => {
            const style = CATEGORY_STYLE[slide.category];
            const photographed = Boolean(slide.imageUrl);
            return (
              <a
                key={slide.href}
                href={slide.href}
                onClick={() => onOpen(slide)}
                aria-label={`Featured: ${slide.title}`}
                className={`relative aspect-[550/350] ${HERO_CARD_WIDTH} shrink-0 snap-start overflow-hidden rounded-[1.75rem] shadow-md focus:outline-none focus-visible:ring-4 focus-visible:ring-[#399918]/20`}
                style={{ background: style.background }}
              >
                {photographed ? (
                  <img src={slide.imageUrl ?? ''} alt="" className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <HeroPreview slide={slide} />
                )}
                <div
                  className={
                    photographed
                      ? 'absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent'
                      : 'absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-white/80 via-white/35 to-transparent'
                  }
                />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p
                    className={`${typeScale.cardCategory} tracking-[0.16em]`}
                    style={{ color: photographed ? 'rgba(255,255,255,0.8)' : style.text }}
                  >
                    Featured
                  </p>
                  <h2
                    className={`mt-1 ${typeScale.heroTitle}`}
                    style={{ color: photographed ? '#fff' : '#1a2a12' }}
                  >
                    {slide.title}
                  </h2>
                  <p
                    className={`mt-1 ${typeScale.heroSubtitle}`}
                    style={{ color: photographed ? 'rgba(255,255,255,0.8)' : style.text }}
                  >
                    {slide.category}
                  </p>
                </div>
                {slide.premium ? (
                  <span className="absolute right-4 top-4 rounded-full bg-white/85 px-2.5 py-1 text-[10px] font-bold text-[#8a5b00]">
                    Premium
                  </span>
                ) : null}
              </a>
            );
          })}
        </div>
      </HubCarouselScroller>
    </section>
  );
}

function HeroPreview({ slide }: { slide: HeroSlide }) {
  if (slide.href.includes('/games/ya-ya')) {
    return (
      <div className="pointer-events-none absolute inset-0 bg-[#3d6b8c]">
        <img src={flappyBgUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <img src={flappyBirdUrl} alt="" className="absolute left-[18%] top-[38%] h-[28%] w-auto object-contain" />
      </div>
    );
  }
  if (slide.category === 'Flashcards' || slide.href.includes('/flashcards/')) {
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[8%] flex h-[52%] items-center justify-center">
        {FLASH_FRAMES.map((frame, index) => (
          <div
            key={frame.id}
            className="absolute aspect-[375/645] w-[26%] max-w-[7.5rem] overflow-hidden rounded-[0.85rem] shadow-lg"
            style={{
              transform: `translateX(${(index - 1) * 58}%) rotate(${[-11, 3, 12][index]}deg)`,
              zIndex: index === 1 ? 2 : 1,
            }}
          >
            <img src={frame.frame} alt="" className="absolute inset-0 h-full w-full object-cover" />
            {index === 1 ? (
              <p className="relative mt-[38%] px-1 text-center text-[11px] font-extrabold leading-tight" style={{ color: frame.ink }}>
                {slide.title}
              </p>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  if (slide.category === 'Grammar' || slide.href.includes('/sheets/')) {
    return (
      <div className="pointer-events-none absolute inset-x-8 top-[16%] space-y-2 sm:inset-x-12">
        {['Nyob zoo', 'Ua tsaug', 'Sib ntsib dua'].map((word) => (
          <div key={word} className="rounded-2xl bg-[#C4EBC0] px-4 py-2.5 text-sm font-extrabold text-[#276e17] shadow-sm">
            {word}
          </div>
        ))}
      </div>
    );
  }

  if (slide.category === 'Games' || slide.category === 'Puzzles') {
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[10%] flex justify-center">
        <div className="grid grid-cols-2 gap-2.5">
          {['Ib', 'Ob', 'Peb', 'Plaub'].map((word) => (
            <span key={word} className="grid h-16 w-[4.6rem] place-items-center rounded-2xl bg-white text-base font-black text-[#276e17] shadow-md">
              {word}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (slide.category === 'Storybooks') {
    return (
      <div className="pointer-events-none absolute inset-x-0 top-[10%] flex justify-center">
        <div className="flex aspect-[3/4] w-[36%] max-w-[9.5rem] flex-col justify-end rounded-l-md rounded-r-2xl bg-white/90 px-3 pb-4 shadow-xl ring-1 ring-black/5">
          <p className="text-center text-sm font-black leading-tight text-[#C05A6E]">{slide.title}</p>
        </div>
      </div>
    );
  }

  return null;
}
